/* eslint-disable no-param-reassign */
import type { AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import axios from 'axios';
import Router from 'next/router';

import GetMSALResponse from '@/src/utils/msalResponseUtil';

import { APP_BASE_API_URL } from './endpoints/apiUrls';
import { BOT_SERVICE_URL } from './endpoints/externalApiUrls';

export const appApiInstance: AxiosInstance = axios.create({
    baseURL: APP_BASE_API_URL,
    validateStatus: (status) => status <= 500,
});

export const apiInstance = axios.create();

const authInterceptor = async (config: InternalAxiosRequestConfig) => {
    try {
        const tokens = await GetMSALResponse();
        const { loginTokenCache, customTokenCache } = tokens;
        if (!config.headers.Authorization) {
            config.headers.Authorization = `Bearer ${loginTokenCache.authenticationResult.accessToken}`;
        }

        if (!config.headers.Authentication) {
            config.headers.Authentication = `SSO ${customTokenCache.authenticationResult.accessToken}`;
        }

        return config;
    } catch (error) {
        throw new Error('Token acquisition error', { cause: error });
    }
};
const responseInterceptor = (response: AxiosResponse) => {
    let data;
    switch (response.status) {
        case 200:
            data = response?.data;
            if (!data) {
                throw new Error('API Error. No data!', { cause: response });
            }
            if (response.config.method === 'get') {
                // Modify response for GET request
            } else if (
                response.config.method === 'post' &&
                (!response.config.responseType || response.config.responseType === 'json')
            ) {
                // Modify response for POST request
                if (response.config.url !== '/reports/export/csv') {
                    response.data = {
                        ...response.data,
                        modified: true,
                    };
                } else if (typeof response.data === 'object' && response.data !== null) {
                    response.data.modified = true;
                }
            }
            break;
        case 201:
            data = response?.data;
            if (!data) {
                throw new Error('API Error. No data!', { cause: response });
            }
            // Handle 201 status code specifically
            response.data = {
                ...response.data,
                message: 'Resource created successfully',
            };
            break;
        case 204:
            data = response?.data;
            response.data = {
                ...response.data,
                code: response.status,
                message: 'Resource deleted successfully',
            };
            break;
        case 400:
            throw new Error(`Bad Request: ${response.data?.message || response.data}`, {
                cause: response,
            });
        case 401:
            Router.push('/401');
            break;
        case 403:
            Router.push('/403');
            break;
        case 404:
            Router.push('/404');
            break;
        default:
            throw new Error('API Error! Invalid status code!', { cause: response });
    }

    return response;
};

appApiInstance.interceptors.request.use(authInterceptor);
appApiInstance.interceptors.response.use(responseInterceptor);

apiInstance.interceptors.request.use(authInterceptor);
apiInstance.interceptors.response.use(responseInterceptor);

export const httpClient = appApiInstance;
export const BotServiceClient = axios.create({
    baseURL: BOT_SERVICE_URL,
});
BotServiceClient.interceptors.request.use(authInterceptor);
BotServiceClient.interceptors.response.use(responseInterceptor);
